import React from "react";
import {
    Alert,
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    Grow,
    Paper,
    Snackbar, Table, TableBody,
    TableCell, TableContainer,
    TableHead, TablePagination,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import MatrixTableFunctionsRow from "../MatrixTableFunctionsRow";
import {visuallyHidden} from "@mui/utils";
import PropTypes from "prop-types";
import UserData from "../../backend/dataservices/UserSettings";
import EmployeeData from "../../backend/dataservices/EmployeeData";
import CourseData from "../../backend/dataservices/CourseData";
import AttendanceData from "../../backend/dataservices/AttendanceData";
import {useNavigate} from "react-router-dom";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TableUtils from "../../utils/TableUtils";
import CreateNewAttendanceDialogComponent from "../CreateNewAttendanceDialogComponent";


function createData(courseName, courseID) {
    return {courseName, courseID};
}

let minColumnWidth = 150;
let minCourseColumnWidth = 250;
let checkboxColumnWidth = 100;

function descendingComparator(a, b, orderBy) {
    if (orderBy === 'date') {
        if (b[orderBy].length >= 11 && a[orderBy].length >= 11) {
            if (b[orderBy].substring(6, 10) < a[orderBy].substring(6, 10)) {
                return -1;
            }
            if (b[orderBy].substring(6, 10) > a[orderBy].substring(6, 10)) {
                return 1;
            }
            return 0;
        }
    }
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;

}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
    const {order, onRequestSort, rowCount, numSelected, onSelectAllClick} =
        props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow sx={{borderBottom: '2px solid gray'}}>
                <TableCell
                    padding={"checkbox"}
                    style={{minWidth: checkboxColumnWidth, position: 'sticky', left: 0, top: 0, zIndex: 2, backgroundColor: 'white'}}>
                    <Checkbox
                        color={"primary"}
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'Alle Kurse wählen',
                        }}
                    />
                </TableCell>

                {props.headCells.map((headCell) => (
                    <TableCell
                        style={{minWidth: headCell.minWidth, zIndex: 2, top: 0, position: 'sticky', left:'courseName' === headCell.id ? checkboxColumnWidth+1 : checkboxColumnWidth + minCourseColumnWidth, backgroundColor: 'white'}}
                        key={headCell.id}
                        align={headCell.numeric ? 'center' : 'center'}
                        padding={'none'}
                        sortDirection={'courseName' === headCell.id ? order : false}
                    >
                        {'courseName' === headCell.id ?
                            <TableSortLabel
                                active={'courseName' === headCell.id}
                                direction={'courseName' === headCell.id ? order : 'asc'}
                                onClick={'courseName' === headCell.id ? createSortHandler(headCell.id) : null}
                            >
                                {headCell.label}
                                {'courseName' === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                            :
                            <Box>
                                {headCell.label}
                            </Box>
                        }
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    onRequestSort: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
    numSelected: PropTypes.number.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
};


export default function TransformedMatrixTable({transformLayout, saveLayout}) {

    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('employeeName');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(100);
    const [rows, setRows] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [myHeadCells, setMyHeadCells] = React.useState([]);
    const [attsSet, setAttsSet] = React.useState(new Set());
    const [newAttendanceData, setNewAttendanceData] = React.useState({});
    const [openNewAttendanceDialog, setOpenNewAttendanceDialog] = React.useState(false);
    const [openDialog, setOpenDialog] = React.useState(false);
    const [dialogData, setDialogData] = React.useState({});
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
    const navigate = useNavigate();


    let headCells = [
        {
            id: 'courseName',
            numeric: true,
            disablePadding: false,
            label: 'Kursname',
            minWidth: minCourseColumnWidth,
        },
    ]

    React.useEffect(() => {
        (async () => {
            loadRows(true);
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function newEmployeeIsNotUsed(employeeID) {
        let found = true;
        headCells.forEach(headCell => {
            if (headCell.id === employeeID)
                found = false;
        });
        return found;
    }

    function loadRows(initData, courses = [], takecourseIDsFromRow = true) {
        let tempRows = [];
        let tempAttsSet = new Set();
        let counter = 0;
        if(initData) {
            UserData.fetchMatrixUserSettings().then(settings => {
                loadAttsForCoursesAndEmployees(tempRows, settings["courses"], counter, tempAttsSet);
            });
        } else {
            if(courses.length === 0 && takecourseIDsFromRow) {
                rows.forEach(row => {
                    courses.push(row.courseID);
                });
            }
            loadAttsForCoursesAndEmployees(tempRows, courses, counter, tempAttsSet);
        }
    }

    function loadAttsForCoursesAndEmployees(tempRows, courses, counter, tempAttsSet) {
        EmployeeData.getAllEmployees().then(employees => {
            if (!Array.isArray(employees)) return;
            employees.forEach(employee => {
                if(newEmployeeIsNotUsed(employee["M_Pers-Nr"])) {
                    headCells.push({
                        id: employee["M_Pers-Nr"],
                        numeric: false,
                        disablePadding: false,
                        label: employee.M_Name + ', ' + employee.M_Vorname,
                        minWidth: minColumnWidth
                    });
                }
            });
            if(courses == null){
                setMyHeadCells(headCells);
                setAttsSet(tempAttsSet);
                setRows(tempRows);
                return;
            }
            courses.forEach(courseID => {
                CourseData.fetchCourseBaseData(courseID).then(course => {
                    tempRows.push(createData(course["KS_Kursname"], courseID));
                    AttendanceData.fetchAllAttendancesForCourse(courseID).then(atts => {
                        if (!Array.isArray(atts)) atts = [];
                        atts.forEach(att => {
                            att.KS_ID = courseID
                        });
                        counter++;
                        tempAttsSet.add(atts);
                        if (counter === courses.length) {
                            let slice = headCells.slice(1, headCells.length).sort((a, b) => a.label.toUpperCase() > b.label.toUpperCase())
                            headCells = headCells.slice(0, 1).concat(slice);
                            setMyHeadCells(headCells);
                            setAttsSet(tempAttsSet);
                            setRows(tempRows);
                        }
                    });
                });
            });
        });
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    function closeSnackbar() {
        setSnackbarOpen(false);
    }

    const toCourses = (courseName, courseID) => {
        navigate("/courses", {state: {id: courseID, name: courseName}});
    }

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n.courseID);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, courseID) => {
        const selectedIndex = selected.indexOf(courseID);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, courseID);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    function deleteAttendance(attendanceID) {
        handleCloseDialog();
        AttendanceData.deletePlannedAttendance(attendanceID).then(value => {
            setSnackbarSeverity("success");
            setSnackbarText("Planung erfoglreich gelöscht");
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
            setSnackbarOpen(true);
        }).then(value => {
            loadRows(false)
        })
    }

    function applyAttendance(attendanceID) {
        handleCloseDialog();
        AttendanceData.updatePlannedAttendance(attendanceID, 'Beantragt').then(value => {
            setSnackbarSeverity("success");
            setSnackbarText("Kurs erfolgreich beantragt");
            setSnackbarOpen(true);
        }, (error) => {
            setSnackbarSeverity("error");
            setSnackbarText("Ein unerwarteter Fehler ist aufgetreten");
            setSnackbarOpen(true);
        }).then(value => {
            loadRows(false)
        })
    }

    const isSelected = (name) => selected.indexOf(name) !== -1;

    function removeSelectedCourses() {
        let filtered = rows.filter(function (value, index, array) {
            return !selected.includes(value.courseID);
        });
        setRows(filtered);
        setSelected([]);
        let courses = [];
        filtered.forEach(filteredRow => {
            courses.push(filteredRow.courseID);
        });
        loadRows(false, courses, false);
    }

    function handleCloseNewAttendanceDialog() {
        setOpenNewAttendanceDialog(false);
    }

    function handleCloseDialog() {
        setOpenDialog(false);
    }

    function handleTableCellClick(courseID, employeeID, employeeName, courseName, status, year, attendanceID) {
        setDialogData({
            courseID: courseID,
            employeeID: employeeID,
            employeeName: employeeName,
            courseName: courseName,
            status: status,
            year: year,
            attendanceID: attendanceID
        });
        if (status === 'Geplant') {
            setOpenDialog(true);
        }
    }

    function courseIsAlreadySelected(courseID) {
        let taken = false;
        rows.forEach(row => {
            if(row["courseID"] === courseID) {
                taken = true;
            }
        });
        return taken;
    }

    function addCourse(name, courseID) {
        if(!courseIsAlreadySelected(courseID)) {
            let newRows = rows;
            rows.push(createData(name, courseID));
            setRows(newRows);
            loadRows(false);
        } else {
            setSnackbarSeverity("error");
            setSnackbarText("Kurs wird bereits angezeigt");
            setSnackbarOpen(true);
        }
    }

    return (
        <Grid container spacing={0} alignItems="center"
              justifyContent="center">
            <Grid item xs={12}>
                <MatrixTableFunctionsRow addCourse={function (courseID, name) {
                    addCourse(name, courseID);
                }} transformLayout={function () {
                    transformLayout();
                }} saveLayout={function () {
                    let courses = [];
                    rows.forEach(row => {
                        courses.push(row.courseID);
                    })
                    saveLayout(courses);
                }}/>
            </Grid>
            <Grid item xs={11} justifyContent={'left'} alignItems={'start'} display={'flex'}>
                <Box>
                    {selected.length === 0 ?
                        <Box height={45}></Box>
                        :
                        <Box height={45} marginLeft={2}>
                            <Button startIcon={<DeleteForeverIcon/>} color={'primary'} variant={'outlined'}
                                    onClick={() => {
                                        removeSelectedCourses();
                                    }
                                    }>Kurse entfernen</Button>
                        </Box>
                    }
                </Box>
            </Grid>

            <Grid item xs={11} maxHeight={600}>
            <Paper sx={{width: '100%', mb: 2, maxHeight: 'inherit'}}>
                <TableContainer sx={{maxHeight: 'inherit'}}>
                    <Table
                        stickyHeader
                        sx={{alignItems: 'center', alignContent: 'center'}}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                            headCells={myHeadCells}
                            numSelected={selected.length}
                            onSelectAllClick={handleSelectAllClick}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 suggestions.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(rows, getComparator(order, orderBy))
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.courseID);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            sx={{height: 50}}
                                            role={'checkbox'}
                                            key={row.courseID}
                                            aria-checked={isItemSelected}
                                        >
                                            <TableCell padding={'checkbox'} style={{minWidth: checkboxColumnWidth, position: 'sticky', zIndex: 1, left: 0, backgroundColor: 'white'}}>
                                                <Checkbox
                                                    color={'primary'}
                                                    checked={isItemSelected}
                                                    inputProps={{
                                                        'aria-labelledby' : labelId,
                                                    }}
                                                    onClick={(event) => {
                                                        handleClick(event, row.courseID);
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                sx={{borderRight: '2px solid gray', textOverflow: 'ellipsis', overflow: 'hidden', position: 'sticky', zIndex: 1, left: checkboxColumnWidth+1, backgroundColor: 'white'}}
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                                style={{cursor: "pointer"}}
                                                onClick={() => {
                                                    toCourses(row.courseName,row.courseID);
                                                }}
                                            >
                                                {row.courseName}
                                            </TableCell>

                                            {myHeadCells.filter(function (value, index, array) {
                                                return index > 0;
                                            }).map(function (value, index, array) {
                                                return TableUtils.getTableCell(row.courseID, myHeadCells[index + 1].id, myHeadCells[index + 1].label, row.courseName, attsSet,
                                                    function (courseID, employeeID, employeeName, courseName, status, year, attendanceID) {
                                                        handleTableCellClick(courseID, employeeID, employeeName, courseName, status, year, attendanceID)
                                                }, function (data) {
                                                        setNewAttendanceData(data);
                                                        setOpenNewAttendanceDialog(true);
                                                    });
                                            })
                                            }


                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 20, 50, 100]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />

                <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={closeSnackbar}
                          anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                    <Alert onClose={closeSnackbar} severity={snackbarSeverity} sx={{width: '100%'}}>
                        {snackbarText}
                    </Alert>
                </Snackbar>
            </Paper>
            </Grid>
            <CreateNewAttendanceDialogComponent
                openDialog={openNewAttendanceDialog}
                handleClose={handleCloseNewAttendanceDialog}
                handleSuccess={() => {
                    setOpenNewAttendanceDialog(false);
                    setSnackbarText("Neue Kursanmeldung für " + newAttendanceData.employeeName + " erfolgreich!");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                    loadRows(false);
                    // handleInputValue(suggestionValue);
                }}
                handleError={() => {
                    setOpenNewAttendanceDialog(false);
                    setSnackbarText("Bei der Kursanmeldung ist ein unerwarteter Fehler aufetreten")
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }}
                knownCourseAndEmployee={true} knownData={newAttendanceData}/>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle sx={{backgroundColor: 'rgb(0,150,136)'}} color={"white"}>
                    Neue Kursanmeldung
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} marginTop={'5%'}>
                        <Grid item xs={12}>
                            Kurs: {dialogData.courseName}
                        </Grid>
                        <Grid item xs={12}>
                            Name: {dialogData.employeeName}
                        </Grid>
                        <Grid item xs={12}>
                            Status: {dialogData.status}
                        </Grid>
                        <Grid item xs={12}>
                            Datum: {dialogData.year}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button style={{color: "gray"}} onClick={handleCloseDialog}>Abbrechen</Button>
                    <Button style={{color: "red"}} onClick={() => deleteAttendance(dialogData.attendanceID)}>Planung
                        löschen</Button>
                    <Button onClick={() => applyAttendance(dialogData.attendanceID)}>Planung beantragen</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}